import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '@/api/axios';
import { HarpErpPulso } from '@/components/HarpErpPulso';
import { RankingSemanalERP } from '@/components/RankingSemanalERP';
import {
  ChartBar, TrendUp, CurrencyDollar, Users, Target, ChartLineUp,
  Phone, MapPin, FileText, ShoppingBag,
} from '@phosphor-icons/react';

const fmtQ = (n) => `Q ${Number(n || 0).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtInt = (n) => Number(n || 0).toLocaleString('es-GT');

const Kpi = ({ icon: Icon, label, value, sub, color = '#1D4ED8' }) => (
  <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-4 flex items-start gap-3" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="w-10 h-10 rounded-[10px] flex items-center justify-center shrink-0" style={{ background: `${color}1A` }}>
      <Icon size={20} weight="fill" style={{ color }} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">{label}</p>
      <p className="text-xl sm:text-2xl font-black text-[var(--text)] heading-font truncate">{value}</p>
      {sub && <p className="text-[11px] text-[var(--text-muted)] mt-0.5">{sub}</p>}
    </div>
  </div>
);

const PeriodPill = ({ value, label, current, onClick }) => (
  <button
    onClick={() => onClick(value)}
    data-testid={`period-${value}`}
    className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${
      current === value
        ? 'bg-emerald-600 text-white shadow'
        : 'bg-[var(--surface-muted)] text-[var(--text-muted)] hover:text-[var(--text)]'
    }`}
  >
    {label}
  </button>
);

export default function DashboardComercial() {
  const { selectedBusiness } = useOutletContext();
  const [period, setPeriod] = useState('month');
  const [scope, setScope] = useState('all'); // 'all' = consolidado de todos mis negocios, 'single' = solo el seleccionado
  const [summary, setSummary] = useState(null);
  const [activities, setActivities] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [topClientes, setTopClientes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedBusiness && scope === 'single') return;
    setLoading(true);
    const params = { period };
    if (scope === 'single' && selectedBusiness) params.business_id = selectedBusiness.id;
    Promise.all([
      api.get('/dashboard-comercial/summary', { params }).then(r => r.data).catch(() => null),
      api.get('/dashboard-comercial/activities', { params }).then(r => r.data).catch(() => null),
      api.get('/dashboard-comercial/pipeline', { params: scope === 'single' ? { business_id: params.business_id } : {} }).then(r => r.data).catch(() => null),
      api.get('/dashboard-comercial/top-clientes', { params: { ...params, limit: 5 } }).then(r => r.data.top).catch(() => []),
    ]).then(([s, act, p, tc]) => {
      setSummary(s);
      setActivities(act);
      setPipeline(p);
      setTopClientes(tc);
      setLoading(false);
    });
  }, [selectedBusiness, period, scope]);

  if (loading) return <div className="p-6 text-[var(--text-muted)]">Cargando Dashboard Comercial…</div>;

  const k = activities?.kpis || {};
  const vendors = activities?.by_vendor || [];
  const daily = activities?.daily || [];
  const periodSub = period === 'today' ? 'hoy' : period === 'week' ? 'esta semana' : 'este mes';

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto" data-testid="page-dashboard-comercial">
      <div className="flex flex-wrap items-start gap-3 mb-5">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shrink-0">
          <ChartLineUp size={26} weight="fill" className="text-white" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <h1 className="text-2xl md:text-3xl font-black text-[var(--text)] heading-font">Dashboard Comercial</h1>
          <p className="text-sm text-[var(--text-muted)] mt-0.5">
            {scope === 'all' ? 'Consolidado de todos tus negocios' : `Solo: ${selectedBusiness?.name || '—'}`} · <b>{summary?.periodo || activities?.periodo || 'Período actual'}</b> · Sin desglose de costos ni márgenes
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="period-selector">
          <div className="flex items-center gap-1 bg-[var(--surface-muted)] rounded-full p-0.5" data-testid="scope-selector">
            <button
              onClick={() => setScope('all')}
              data-testid="scope-all"
              className={`px-3 py-1 rounded-full text-xs font-bold transition ${scope === 'all' ? 'bg-[var(--surface)] text-[var(--text)] shadow-sm' : 'text-[var(--text-muted)] hover:text-[var(--text)]'}`}
            >
              Todos mis negocios
            </button>
            <button
              onClick={() => setScope('single')}
              data-testid="scope-single"
              className={`px-3 py-1 rounded-full text-xs font-bold transition ${scope === 'single' ? 'bg-[var(--surface)] text-[var(--text)] shadow-sm' : 'text-[var(--text-muted)] hover:text-[var(--text)]'}`}
            >
              Solo este negocio
            </button>
          </div>
          <PeriodPill value="today" label="Hoy" current={period} onClick={setPeriod} />
          <PeriodPill value="week" label="Semana" current={period} onClick={setPeriod} />
          <PeriodPill value="month" label="Mes" current={period} onClick={setPeriod} />
        </div>
      </div>

      {/* ▸ Actividad del equipo (lo que suben los vendedores) */}
      <h2 className="text-sm font-black uppercase tracking-wider text-[var(--text-muted)] mb-2 mt-1">Actividad del equipo · {periodSub}</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi icon={Phone} label="Llamadas" value={fmtInt(k.calls)} sub="registradas por vendedores" color="#0EA5E9" />
        <Kpi icon={MapPin} label="Visitas" value={fmtInt(k.visits)} sub="realizadas en campo" color="#F97316" />
        <Kpi icon={FileText} label="Cotizaciones" value={fmtInt(k.quotes)} sub={fmtQ(k.amount_quoted)} color="#8B5CF6" />
        <Kpi icon={ShoppingBag} label="Ventas" value={fmtInt(k.sales)} sub={fmtQ(k.amount_sold)} color="#10B981" />
      </div>

      {/* ▸ KPIs comerciales (ventas + conversión) */}
      <h2 className="text-sm font-black uppercase tracking-wider text-[var(--text-muted)] mb-2">Ventas y conversión</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi icon={CurrencyDollar} label="Ventas del período" value={fmtQ(summary?.ventas_total_q)} sub={`${summary?.num_ventas || 0} ventas registradas en CRM`} color="#10B981" />
        <Kpi icon={TrendUp} label="Ticket promedio" value={fmtQ(summary?.ticket_promedio_q)} color="#3B82F6" />
        <Kpi icon={Target} label="Conversión total" value={`${summary?.conversion_total_pct || 0}%`} sub="conversaciones → venta" color="#F59E0B" />
        <Kpi icon={ChartBar} label="Cotización → venta" value={`${summary?.conversion_cotizacion_a_venta_pct || 0}%`} sub={`${summary?.cotizaciones_aprobadas || 0} / ${summary?.cotizaciones_creadas || 0}`} color="#8B5CF6" />
      </div>

      {/* ▸ Ventas HarpERP (facturación real, ambas empresas) */}
      <HarpErpPulso defaultPeriod={period === 'week' ? 'week' : period === 'month' ? 'month' : 'today'} />

      {/* ▸ Ranking de la semana por facturación ERP */}
      <RankingSemanalERP />

      {/* ▸ KPIs secundarios */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi icon={Users} label="Conversaciones" value={summary?.conversaciones_nuevas || 0} sub={`nuevas ${periodSub}`} color="#06B6D4" />
        <Kpi icon={Users} label="Prospectos calientes" value={summary?.prospectos_calientes || 0} sub="IA score ≥ 70" color="#EF4444" />
        <Kpi icon={ChartBar} label="Cotizaciones creadas" value={summary?.cotizaciones_creadas || 0} color="#6366F1" />
        <Kpi icon={Target} label="Pipeline activo" value={fmtQ(pipeline?.monto_total_q)} sub={`${pipeline?.num_cotizaciones || 0} cotizaciones`} color="#EC4899" />
      </div>

      {/* ▸ Ranking de vendedores con desglose de actividades */}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-4 mb-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-black text-[var(--text)] heading-font">🏆 Ranking de Vendedores · {periodSub}</h2>
          <span className="text-[11px] text-[var(--text-muted)]">{vendors.length} vendedor(es) activo(s)</span>
        </div>
        {vendors.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)] italic py-3">Sin actividad registrada {periodSub}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ranking-vendedores-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] border-b border-[var(--border)]">
                  <th className="text-left py-2 pr-2">#</th>
                  <th className="text-left py-2 pr-2">Vendedor</th>
                  <th className="text-right py-2 px-2"><Phone size={12} className="inline mr-1" />Llamadas</th>
                  <th className="text-right py-2 px-2"><MapPin size={12} className="inline mr-1" />Visitas</th>
                  <th className="text-right py-2 px-2"><FileText size={12} className="inline mr-1" />Cotiz.</th>
                  <th className="text-right py-2 px-2"><ShoppingBag size={12} className="inline mr-1" />Ventas</th>
                  <th className="text-right py-2 pl-2">Monto vendido</th>
                </tr>
              </thead>
              <tbody>
                {vendors.map((v, i) => (
                  <tr key={v.vendor_id || i} className="border-b border-[var(--border)]/40 hover:bg-[var(--surface-muted)]" data-testid={`ranking-row-${i}`}>
                    <td className="py-2 pr-2 font-black text-[var(--text-muted)]">#{i + 1}</td>
                    <td className="py-2 pr-2 font-bold text-[var(--text)] truncate max-w-[220px]">{v.vendor_name}</td>
                    <td className="py-2 px-2 text-right text-sky-600 font-bold">{fmtInt(v.call)}</td>
                    <td className="py-2 px-2 text-right text-orange-600 font-bold">{fmtInt(v.visit)}</td>
                    <td className="py-2 px-2 text-right text-violet-600 font-bold">{fmtInt(v.quote)}</td>
                    <td className="py-2 px-2 text-right text-emerald-600 font-bold">{fmtInt(v.sale)}</td>
                    <td className="py-2 pl-2 text-right font-black text-emerald-600">{fmtQ(v.amount_sold)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Top clientes */}
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-4">
          <h2 className="text-base font-black text-[var(--text)] heading-font mb-3">⭐ Top 5 Clientes · {periodSub}</h2>
          {topClientes.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)] italic">Sin compras registradas {periodSub}</p>
          ) : (
            <div className="space-y-2">
              {topClientes.map((c, i) => (
                <div key={c.customer_id || i} className="flex items-center gap-3 p-2 rounded-[8px] hover:bg-[var(--surface-muted)]">
                  <span className="text-lg font-black text-[var(--text-muted)] w-6 text-center">#{i + 1}</span>
                  <span className="flex-1 font-bold text-[var(--text)] truncate">{c.cliente_nombre}</span>
                  <div className="text-right">
                    <p className="text-sm font-black text-emerald-600">{fmtQ(c.total_q)}</p>
                    <p className="text-[10px] text-[var(--text-muted)]">{c.num_compras} compra(s)</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top 5 pipeline */}
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-4">
          <h2 className="text-base font-black text-[var(--text)] heading-font mb-3">📋 Top 5 Cotizaciones en Pipeline</h2>
          {(!pipeline?.top_5_pipeline || pipeline.top_5_pipeline.length === 0) ? (
            <p className="text-sm text-[var(--text-muted)] italic">Sin cotizaciones activas en pipeline</p>
          ) : (
            <div className="space-y-2">
              {pipeline.top_5_pipeline.map((c, i) => (
                <div key={c.numero || i} className="flex items-center gap-3 p-2 rounded-[8px] hover:bg-[var(--surface-muted)] flex-wrap">
                  <span className="text-[10px] px-2 py-0.5 bg-[#1D4ED8]/10 text-[#1D4ED8] rounded font-bold font-mono">{c.numero || '—'}</span>
                  <span className="flex-1 min-w-[140px] font-bold text-[var(--text)] truncate">{c.cliente_nombre || '(sin cliente)'}</span>
                  <span className="text-[10px] px-2 py-0.5 bg-amber-500/10 text-amber-700 dark:text-amber-300 rounded uppercase font-bold">{c.estado}</span>
                  <span className="text-sm font-black text-emerald-600">{fmtQ(c.total)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ▸ Actividad diaria */}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-4 mt-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-black text-[var(--text)] heading-font">📅 Actividad diaria · {periodSub}</h2>
          <span className="text-[11px] text-[var(--text-muted)]">{daily.length} día(s) con actividad</span>
        </div>
        {daily.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)] italic py-3">Sin actividad registrada {periodSub}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="daily-activity-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] border-b border-[var(--border)]">
                  <th className="text-left py-2 pr-2">Fecha</th>
                  <th className="text-right py-2 px-2">Llamadas</th>
                  <th className="text-right py-2 px-2">Visitas</th>
                  <th className="text-right py-2 px-2">Cotiz.</th>
                  <th className="text-right py-2 px-2">Ventas</th>
                  <th className="text-right py-2 px-2">Total</th>
                  <th className="text-right py-2 pl-2">Monto</th>
                </tr>
              </thead>
              <tbody>
                {daily.map((d) => (
                  <tr key={d.date} className="border-b border-[var(--border)]/40 hover:bg-[var(--surface-muted)]" data-testid={`daily-row-${d.date}`}>
                    <td className="py-2 pr-2 font-mono text-[var(--text-muted)]">{d.date}</td>
                    <td className="py-2 px-2 text-right text-sky-600 font-bold">{fmtInt(d.call)}</td>
                    <td className="py-2 px-2 text-right text-orange-600 font-bold">{fmtInt(d.visit)}</td>
                    <td className="py-2 px-2 text-right text-violet-600 font-bold">{fmtInt(d.quote)}</td>
                    <td className="py-2 px-2 text-right text-emerald-600 font-bold">{fmtInt(d.sale)}</td>
                    <td className="py-2 px-2 text-right font-black text-[var(--text)]">{fmtInt(d.total)}</td>
                    <td className="py-2 pl-2 text-right font-black text-emerald-600">{fmtQ(d.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
